import { ApiProvider } from '@/provider/ApiProvider';
import {
  type ActiveAssignment,
  type ActiveContext,
  type AssignmentList,
  type AssignmentSummary,
  assignmentFromLoginContext,
  getRoleStateOwner,
  getTabAssignment,
  setActiveAssignment,
  toActiveAssignment,
} from '@/auth/activeAssignment';
import { clearSession } from '@/auth/clearSession';
import { canOpenPath } from '@/auth/pathGates';
import {
  clearPreviewPages,
  markLanding,
  notePreviewRunning,
  pageOf,
  pauseRolePages,
} from '@/auth/rolePages';
import { announceRoleChange, markSwitchingHere } from '@/auth/roleChange';
import {
  LOADING_ROLE_MESSAGE,
  endRoleSwitch,
  showRoleSwitchMessage,
  startRoleSwitch,
} from '@/auth/roleSwitchProgress';
import { readSessionData, subscribeSession, writeSessionData } from '@/auth/session';
import { sessionKeys } from '@/query/keys';
import {
  type HeaderEntity,
  flushHeaderSelection,
  setHeaderBrandSelection,
  setHeaderPropertySelection,
} from '@/selection/headerSelection';

/**
 * Choosing the active role assignment (Authorization-Plan §6.2, §13.6):
 *
 *   GET  /identity/api/me/assignments            valid assignments + active id
 *   POST /identity/api/me/session/assignment     {role_assignment_id}
 *
 * - at sign-in (usePostLogin -> ApiProvider.signInUserWithUserData):
 *   0 valid -> "no access"; 1 -> selected (POST when not active yet);
 *   several and none active -> the selection screen; then the login continues;
 * - at every page load (RoleGate): the same, plus "the role was switched
 *   elsewhere" -> role state reset and a reload;
 * - in the sidebar role menu (SidebarRoleMenu): POST, reset, announce, reload.
 *
 * After every POST (and at page load) the bearer token is made to carry the
 * new active assignment (`pnet:ra`, ApiProvider.ensureTokenForAssignment)
 * BEFORE the first request of that role: the servers compare it with the
 * tab's X-PNET-Expected-Assignment and answer 409 `role_changed` otherwise.
 */

// ---- the list of this user's assignments (for the switcher) ----------------------------

// Kept in the session (query cache, memory only): GET me/assignments' list.
const NO_ASSIGNMENTS: AssignmentSummary[] = [];

export const getAssignmentsSnapshot = (): AssignmentSummary[] =>
  readSessionData<AssignmentSummary[]>(sessionKeys.assignments) ?? NO_ASSIGNMENTS;

export const subscribeAssignments = (
  listener: (list: AssignmentSummary[]) => void,
): (() => void) => {
  let last = getAssignmentsSnapshot();
  return subscribeSession(() => {
    const next = getAssignmentsSnapshot();
    if (next === last) return;
    last = next;
    listener(next);
  });
};

const setAssignmentsSnapshot = (list: AssignmentSummary[]) => {
  writeSessionData(sessionKeys.assignments, list);
};

// ---- the selection screen request (rendered by RoleSelectionHost) ------------------------

export interface ChoiceRequest {
  assignments: AssignmentSummary[];
  resolve: (assignment: AssignmentSummary) => void;
}

let pendingChoice: ChoiceRequest | null = null;
const choiceListeners = new Set<(request: ChoiceRequest | null) => void>();

export const getPendingChoice = (): ChoiceRequest | null => pendingChoice;

export const subscribeChoice = (
  listener: (request: ChoiceRequest | null) => void,
): (() => void) => {
  choiceListeners.add(listener);
  return () => {
    choiceListeners.delete(listener);
  };
};

const setPendingChoice = (request: ChoiceRequest | null) => {
  pendingChoice = request;
  choiceListeners.forEach((listener) => listener(request));
};

/** Shows the selection screen and resolves with the user's choice. */
export const requestRoleChoice = (assignments: AssignmentSummary[]): Promise<AssignmentSummary> =>
  new Promise((resolve) => {
    setPendingChoice({
      assignments,
      resolve: (assignment) => {
        setPendingChoice(null);
        resolve(assignment);
      },
    });
  });

export type Chooser = (assignments: AssignmentSummary[]) => Promise<AssignmentSummary>;

// ---- decisions ------------------------------------------------------------------------------

export type Resolution =
  | { kind: 'none' }
  | { kind: 'active'; assignment: AssignmentSummary }
  | { kind: 'select'; assignment: AssignmentSummary }
  | { kind: 'choose'; assignments: AssignmentSummary[] };

/**
 * What to do with GET me/assignments (the server already dropped expired,
 * disabled and foreign assignments): none -> no access; the active one ->
 * use it; exactly one -> select it; several and none active -> ask.
 */
export function resolveAssignments(
  list: Pick<AssignmentList, 'assignments' | 'active_assignment_id'>,
): Resolution {
  const assignments = Array.isArray(list?.assignments) ? list.assignments : [];
  if (assignments.length === 0) return { kind: 'none' };
  const active = assignments.find((a) => a.id === list.active_assignment_id);
  if (active) return { kind: 'active', assignment: active };
  if (assignments.length === 1) return { kind: 'select', assignment: assignments[0] };
  return { kind: 'choose', assignments };
}

// ---- server calls ------------------------------------------------------------------------------

export type FetchOutcome =
  | { ok: true; list: AssignmentList }
  | { ok: false; noAccess: boolean; message: string };

/**
 * Refusals of me/assignments that mean "this user cannot use the
 * application": user_inactive, no_assignment, and organisation_inactive (the
 * organisation is a former client, an ended trial or a prospect; owner
 * decision 2026-10-07). Anything else is "unavailable".
 */
export const NO_ACCESS_REASONS = ['user_inactive', 'no_assignment', 'organisation_inactive'];

let noAccessReason: string | null = null;

/** Why the last role check said "no access" (the no-access page's message). */
export const lastNoAccessReason = (): string | null => noAccessReason;

/** This UI's application (applications.key_value of PartnershipNet). */
export const PNET_APPLICATION = 'partner';

/**
 * An assignment this UI can use: its role belongs to PartnershipNet. A role of
 * another application only (DMC, Arena) or of none opens nothing here (every
 * server refuses it), so it is never selected, offered or kept active
 * (QA 2026-10-08: a role without an application landed on a page where every
 * call was refused instead of "no access"). Without the field (an older
 * identity service) the assignment is kept.
 */
export const usableHere = (a: AssignmentSummary): boolean =>
  !Array.isArray(a?.applications) || a.applications.includes(PNET_APPLICATION);

export async function fetchAssignments(): Promise<FetchOutcome> {
  const response = await ApiProvider.identityRequest('GET', 'me/assignments');
  if (response && Array.isArray(response.assignments)) {
    const list = response as AssignmentList;
    return { ok: true, list: { ...list, assignments: list.assignments.filter(usableHere) } };
  }
  const message = String(response?.message ?? 'unavailable');
  const noAccess = NO_ACCESS_REASONS.includes(message);
  if (noAccess) noAccessReason = message;
  return { ok: false, noAccess, message };
}

/**
 * POST the choice; the new active assignment's context when the server
 * accepted it (`active_context` of the answer), else null.
 */
export async function selectAssignment(id: number): Promise<ActiveContext | null> {
  const response = await ApiProvider.identityRequest('POST', 'me/session/assignment', {
    role_assignment_id: id,
  });
  if (Number(response?.role_assignment_id) !== id) return null;
  const context = response?.active_context;
  return context && typeof context === 'object' ? (context as ActiveContext) : null;
}

/** The signed-in user's id (the session user is set before the role is chosen). */
const currentUserId = (): number | null => Number(ApiProvider.returnUserData()?.id) || null;

// ---- flows ------------------------------------------------------------------------------------

/**
 * - `ok`: `assignment` is active on the server (and returned); `tokenEnsured`:
 *   it was POSTed here and the token already refreshed for it;
 * - `no_access`: the user has no valid assignment (or is inactive);
 * - `unavailable`: the identity service is not configured or did not answer;
 *   the caller continues WITHOUT an assignment (no header is sent, the old
 *   per-user behaviour applies; the servers stay the authority).
 */
export type EnsureOutcome =
  | { status: 'ok'; assignment: ActiveAssignment; tokenEnsured: boolean }
  | { status: 'no_access' }
  | { status: 'unavailable'; message: string };

/** Chooses the active assignment with the server (does not store it). */
export async function ensureActiveAssignment(
  choose: Chooser = requestRoleChoice,
): Promise<EnsureOutcome> {
  const fetched = await fetchAssignments();
  if (!fetched.ok) {
    return fetched.noAccess
      ? { status: 'no_access' }
      : { status: 'unavailable', message: fetched.message };
  }
  setAssignmentsSnapshot(fetched.list.assignments);
  const userId = currentUserId();
  const decision = resolveAssignments(fetched.list);

  let chosen: AssignmentSummary;
  let context: unknown;
  let tokenEnsured = false;
  if (decision.kind === 'none') return { status: 'no_access' };
  if (decision.kind === 'active') {
    chosen = decision.assignment;
    context = fetched.list.active_context;
  } else {
    chosen = decision.kind === 'select' ? decision.assignment : await choose(decision.assignments);
    context = await selectAssignment(chosen.id);
    if (!context) {
      return { status: 'unavailable', message: 'selection_failed' };
    }
    // the token of the sign-in still names the previous (or no) assignment
    await ApiProvider.ensureTokenForAssignment(chosen.id);
    tokenEnsured = true;
    setAssignmentsSnapshot(
      fetched.list.assignments.map((a) => ({ ...a, is_active: a.id === chosen.id })),
    );
  }
  const assignment = toActiveAssignment(chosen, context, userId);
  return assignment
    ? { status: 'ok', assignment, tokenEnsured }
    : { status: 'unavailable', message: 'invalid_assignment' };
}

/**
 * The sign-in step (ApiProvider.signInUserWithUserData `resolveAssignment`):
 * true = continue the login (with an assignment, or without one when the
 * identity service is unavailable), false = no access.
 */
export async function resolveAssignmentAtSignIn(
  choose: Chooser = requestRoleChoice,
): Promise<boolean> {
  const outcome = await ensureActiveAssignment(choose);
  if (outcome.status === 'no_access') {
    // nothing of a role stays in memory or storage; the user stays signed in
    clearSession('no_access');
    return false;
  }
  if (outcome.status === 'ok') setActiveAssignment(outcome.assignment);
  return true;
}

/** How long a role switch waits for the header selection to be saved before it reloads. */
export const FLUSH_BEFORE_RELOAD_MS = 3000;

/** Sets the header context the way useGlobalFilters does, without React (before a reload). */
const storeHeaderBrand = (brand: HeaderEntity | null) => {
  if (brand && brand.id !== 'clear') setHeaderBrandSelection(brand);
};
const storeHeaderProperty = (property: HeaderEntity | null) => {
  if (property && property.id !== 'clear') setHeaderPropertySelection(property);
};

/**
 * Makes `assignment` the browser's role: clears the role-dependent state,
 * stores the assignment, loads the role's data (permissions, brands, ...)
 * and tells the other tabs. The caller reloads the page afterwards.
 *
 * The page on screen belongs to the previous role: from here until the reload
 * nothing is noted as a role's page (auth/rolePages.ts). Its route gate
 * decides again once the new role's permissions arrive, and used to redirect
 * to /nopermission (or remount the page) under the new role, whose header then
 * wrote that address to the shared `sessionurl` the reload landed on.
 */
export async function adoptAssignment(
  assignment: ActiveAssignment,
  announce: boolean,
): Promise<void> {
  pauseRolePages();
  // the previous role's data (query cache, session, browser storage); the user stays
  clearSession('switch');
  // a switch ends a running role preview (identity service): its pages go with it, and the
  // new role lands on its own page
  clearPreviewPages();
  notePreviewRunning(false);
  setActiveAssignment(assignment);
  const user = ApiProvider.returnUserData();
  if (user?.id) {
    // the role's last header selection (server), then the scope's only brand / partner
    await ApiProvider.loadHeaderSelection();
    await ApiProvider.loadRoleData(Number(user.id), storeHeaderBrand, storeHeaderProperty);
    // the caller reloads the page: a change still waiting would be lost with the memory
    // (bounded: a store that does not answer never holds the switch up)
    let timer: ReturnType<typeof setTimeout> | undefined;
    await Promise.race([
      flushHeaderSelection(),
      new Promise((resolve) => {
        timer = setTimeout(resolve, FLUSH_BEFORE_RELOAD_MS);
      }),
    ]);
    clearTimeout(timer);
  }
  if (announce) {
    announceRoleChange({
      userId: assignment.userId ?? (Number(user?.id) || null),
      assignmentId: assignment.id,
      // the label names the role and the organisation
      roleName: assignment.label,
      contextName: null,
    });
  }
}

/**
 * Full page load on the new role's landing page: its own last page when it may
 * open it, else its home (ApiProvider.getRedirectionUrl; never the previous
 * role's page). Its permissions are loaded already. Marked as the role's first
 * landing: a gate that still refuses it goes home, not to /nopermission.
 */
export const reloadToLanding = (): void => {
  const landing = ApiProvider.getRedirectionUrl(ApiProvider.returnUserData() ?? {}, canOpenPath);
  markLanding(landing);
  window.location.replace(`${window.location.pathname}#${landing}`);
  window.location.reload();
};

/**
 * The header switcher: POST the new choice, get a token of the new role,
 * reset and reload the role's data, announce it to the other tabs, full
 * reload. The page is covered meanwhile (RoleSwitchOverlay). False when the
 * server refused or the switch failed: the cover goes away again.
 */
export async function switchAssignment(target: AssignmentSummary): Promise<boolean> {
  startRoleSwitch(target.label);
  markSwitchingHere(true);
  const fail = () => {
    markSwitchingHere(false);
    pauseRolePages(false);
    endRoleSwitch();
    return false;
  };
  try {
    const context = await selectAssignment(target.id);
    const assignment = context ? toActiveAssignment(target, context, currentUserId()) : null;
    if (!assignment) return fail();
    // before the first request with the new header (adoptAssignment loads the role's data)
    await ApiProvider.ensureTokenForAssignment(assignment.id);
    await adoptAssignment(assignment, true);
  } catch {
    return fail();
  }
  // the cover stays until the page is gone
  reloadToLanding();
  return true;
}

// ---- page load -------------------------------------------------------------------------------

export type BootstrapOutcome = 'ready' | 'no_access' | 'reloading';

/**
 * Page load of a signed-in tab: confirm the active assignment with the server
 * (the session is in memory, so every page load starts without one).
 * - the server's active assignment becomes this tab's (header on);
 * - the page context kept in localStorage was written under ANOTHER
 *   assignment (the role was switched on another device, or in a tab of
 *   another browser): reset + reload the role's data, then reload the page;
 * - identity service unavailable: LoginData's `active_context` when the page
 *   load got one, else run without an assignment.
 */
export async function bootstrapAssignment(
  choose: Chooser = requestRoleChoice,
): Promise<BootstrapOutcome> {
  if (getTabAssignment()) return 'ready'; // set by the sign-in flow in this page
  const stateOwner = getRoleStateOwner();
  const outcome = await ensureActiveAssignment(choose);
  if (outcome.status === 'no_access') {
    clearSession('no_access');
    return 'no_access';
  }
  if (outcome.status === 'unavailable') {
    const fromLogin = assignmentFromLoginContext(
      readSessionData(sessionKeys.loginContext),
      currentUserId(),
    );
    if (fromLogin) setActiveAssignment(fromLogin);
    return 'ready';
  }
  // the token may predate a switch on another device: it must name this assignment
  if (!outcome.tokenEnsured) await ApiProvider.ensureTokenForAssignment(outcome.assignment.id);
  if (stateOwner !== null && stateOwner !== outcome.assignment.id) {
    showRoleSwitchMessage(LOADING_ROLE_MESSAGE);
    try {
      await adoptAssignment(outcome.assignment, false);
    } catch (error) {
      pauseRolePages(false);
      endRoleSwitch();
      throw error;
    }
    // the same address under the adopted role: a gate that refuses it goes home
    const here = pageOf(window.location.hash);
    if (here) markLanding(here);
    window.location.reload();
    return 'reloading';
  }
  setActiveAssignment(outcome.assignment);
  return 'ready';
}
